'use client';

import React, { useState } from 'react';
import {
  Building2, Plus, Search, ChevronDown, ChevronRight,
  MapPin, Users, Bird, Home, Layers, GitBranch,
  MoreVertical, Eye, Edit2, Trash2, Filter
} from 'lucide-react';
import {
  MOCK_FARMS, Farm, Branch,
  FARM_TYPE_LABELS, FARM_TYPE_COLORS, STATUS_COLORS,
  FarmScopeContext, DEFAULT_SCOPE
} from './farmsData';
import FarmsSummaryCards from './FarmsSummaryCards';
import FarmProfileModal from './FarmProfileModal';
import CreateFarmModal from './CreateFarmModal';
import CreateBranchModal from './CreateBranchModal';
import Icon from '@/components/ui/AppIcon';
import { useRole } from '@/lib/useRole';


export default function FarmsContent() {
  const [farms, setFarms] = useState(MOCK_FARMS);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [expandedFarms, setExpandedFarms] = useState<Set<string>>(new Set(['farm-001', 'farm-002']));
  const [selectedFarm, setSelectedFarm] = useState<Farm | null>(null);
  const [showCreateFarm, setShowCreateFarm] = useState(false);
  const [showCreateBranch, setShowCreateBranch] = useState(false);
  const [createBranchFarmId, setCreateBranchFarmId] = useState<string>('');
  const [scopeContext, setScopeContext] = useState<FarmScopeContext>(DEFAULT_SCOPE);
  const [toast, setToast] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const { can } = useRole();

  const canManageFarms = can('manage_farms');

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  }

  function toggleExpand(farmId: string) {
    setExpandedFarms(prev => {
      const next = new Set(prev);
      if (next.has(farmId)) next.delete(farmId);
      else next.add(farmId);
      return next;
    });
  }

  function handleScopeSelect(farmId: string | null, branchId: string | null) {
    if (!farmId) {
      setScopeContext(DEFAULT_SCOPE);
      showToast('Scope reset — showing all farms and branches.');
      return;
    }
    const farm = farms.find(f => f.id === farmId);
    if (!farm) return;
    if (branchId) {
      const branch = farm.branches.find(b => b.id === branchId);
      setScopeContext({ farmId, branchId, farmName: farm.farmName, branchName: branch?.branchName || '' });
      showToast(`Scope set to: ${farm.farmName} › ${branch?.branchName}. Downstream operations will be filtered.`);
    } else {
      setScopeContext({ farmId, branchId: null, farmName: farm.farmName, branchName: 'All Branches' });
      showToast(`Scope set to: ${farm.farmName}. All branches included.`);
    }
  }

  function handleSaveFarm(data: Partial<Farm>) {
    const newFarm: Farm = {
      id: `farm-${Date.now()}`,
      farmCode: data.farmCode || '',
      farmName: data.farmName || '',
      farmType: data.farmType || 'mixed',
      owner: data.owner || '',
      manager: data.manager || '',
      managerPhone: data.managerPhone || '',
      managerEmail: data.managerEmail || '',
      country: data.country || 'Uganda',
      district: data.district || '',
      subcounty: data.subcounty || '',
      village: data.village || '',
      gpsLat: data.gpsLat || '',
      gpsLng: data.gpsLng || '',
      phone: data.phone || '',
      email: data.email || '',
      openingDate: data.openingDate || '',
      totalCapacity: data.totalCapacity || 0,
      currentBirds: 0,
      activeHouses: 0,
      activeFlocks: 0,
      totalEmployees: 0,
      status: data.status || 'active',
      branches: [],
      monthlyRevenue: 0,
      monthlyExpenses: 0,
      eggsTodayCount: 0,
      mortalityToday: 0,
      feedInStock: 0,
    };
    setFarms(prev => [...prev, newFarm]);
    setShowCreateFarm(false);
    showToast(`Farm "${newFarm.farmName}" created successfully.`);
  }

  function handleSaveBranch(data: Partial<Branch>) {
    setFarms(prev => prev.map(f => {
      if (f.id !== createBranchFarmId) return f;
      const newBranch: Branch = {
        id: `branch-${Date.now()}`,
        branchCode: data.branchCode || '',
        branchName: data.branchName || '',
        farmId: createBranchFarmId,
        manager: data.manager || '',
        managerPhone: data.managerPhone || '',
        country: data.country || 'Uganda',
        district: data.district || '',
        subcounty: data.subcounty || '',
        village: data.village || '',
        gpsLat: data.gpsLat || '',
        gpsLng: data.gpsLng || '',
        phone: data.phone || '',
        email: data.email || '',
        openingDate: data.openingDate || '',
        capacity: data.capacity || 0,
        currentBirds: 0,
        activeHouses: 0,
        activeFlocks: 0,
        status: data.status || 'active',
        notes: data.notes || '',
      };
      return { ...f, branches: [...f.branches, newBranch] };
    }));
    setShowCreateBranch(false);
    showToast('Branch added successfully.');
  }

  const filtered = farms.filter(f => {
    const matchSearch = !search || f.farmName.toLowerCase().includes(search.toLowerCase()) || f.farmCode.toLowerCase().includes(search.toLowerCase()) || f.district.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || f.status === statusFilter;
    const matchType = typeFilter === 'all' || f.farmType === typeFilter;
    return matchSearch && matchStatus && matchType;
  });

  const isScopeActive = scopeContext.farmId !== null;

  return (
    <div className="p-6">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-3 bg-gray-900 text-white text-sm px-4 py-3 rounded-xl shadow-xl max-w-sm fade-in">
          <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" />
          {toast}
        </div>
      )}

      {/* Page Header */}
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Farms & Branches</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage farm profiles, branch nesting, capacity, location, and manager assignments. Select a farm or branch to scope downstream operations.
          </p>
        </div>
        {canManageFarms && (
          <button
            onClick={() => setShowCreateFarm(true)}
            className="btn-primary flex-shrink-0"
          >
            <Plus size={15} />
            Add Farm
          </button>
        )}
      </div>

      {/* Summary Cards */}
      <FarmsSummaryCards />

      {/* Active Scope Banner */}
      {isScopeActive && (
        <div className="mb-4 flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
          <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-green-800">
              Active Scope: {scopeContext.farmName}
              {scopeContext.branchId && ` › ${scopeContext.branchName}`}
            </p>
            <p className="text-xs text-green-600">Downstream operations (flocks, feed, health, sales) are filtered to this scope.</p>
          </div>
          <button
            onClick={() => handleScopeSelect(null, null)}
            className="text-xs text-green-700 font-semibold hover:text-green-900 flex-shrink-0"
          >
            Clear Scope
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search farms by name, code, district…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="input-field pl-8 text-sm"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-gray-400" />
          <select className="select-field w-auto text-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>
          <select className="select-field w-auto text-sm" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
            <option value="all">All Types</option>
            <option value="broiler">Broiler</option>
            <option value="layer">Layer</option>
            <option value="breeder">Breeder</option>
            <option value="indigenous">Indigenous</option>
            <option value="mixed">Mixed</option>
          </select>
        </div>
      </div>

      {/* Farm Hierarchy List */}
      <div className="space-y-3">
        {filtered.length === 0 && (
          <div className="text-center py-16 text-gray-400">
            <Building2 size={36} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">No farms match your search.</p>
          </div>
        )}

        {filtered.map(farm => {
          const isExpanded = expandedFarms.has(farm.id);
          const isScoped = scopeContext.farmId === farm.id;
          const occupancyPct = farm.totalCapacity > 0 ? Math.round((farm.currentBirds / farm.totalCapacity) * 100) : 0;

          return (
            <div
              key={farm.id}
              className={`panel-card overflow-hidden transition-all ${isScoped ? 'ring-2 ring-green-400' : ''}`}
            >
              {/* Farm Row */}
              <div className="flex items-center gap-3 p-4">
                {/* Expand toggle */}
                <button
                  onClick={() => toggleExpand(farm.id)}
                  className="w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 flex-shrink-0 transition-colors"
                >
                  {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>

                {/* Farm icon */}
                <div className="w-9 h-9 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Building2 size={16} className="text-green-700" />
                </div>

                {/* Farm info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-gray-900">{farm.farmName}</span>
                    <span className="text-xs text-gray-400 font-mono">{farm.farmCode}</span>
                    <span className={`status-badge ${FARM_TYPE_COLORS[farm.farmType]}`}>{FARM_TYPE_LABELS[farm.farmType]}</span>
                    <span className={`status-badge ${STATUS_COLORS[farm.status]}`}>{farm.status.charAt(0).toUpperCase() + farm.status.slice(1)}</span>
                    {isScoped && !scopeContext.branchId && (
                      <span className="status-badge bg-green-600 text-white">Scoped</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-1 flex-wrap">
                    <span className="flex items-center gap-1 text-xs text-gray-400">
                      <MapPin size={11} />{farm.district}, {farm.country}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-gray-400">
                      <Users size={11} />{farm.manager}
                    </span>
                  </div>
                </div>

                {/* Stats */}
                <div className="hidden md:flex items-center gap-4 flex-shrink-0">
                  {[
                    { icon: Bird, value: farm.currentBirds.toLocaleString(), label: 'birds' },
                    { icon: Home, value: String(farm.activeHouses), label: 'houses' },
                    { icon: Layers, value: String(farm.activeFlocks), label: 'flocks' },
                    { icon: GitBranch, value: String(farm.branches.length), label: 'branches' },
                  ].map(stat => {
                    const Icon = stat.icon;
                    return (
                      <div key={stat.label} className="text-center">
                        <div className="flex items-center gap-1 text-xs font-bold text-gray-700">
                          <Icon size={11} className="text-gray-400" />
                          {stat.value}
                        </div>
                        <p className="text-xs text-gray-400">{stat.label}</p>
                      </div>
                    );
                  })}
                  {/* Occupancy bar */}
                  <div className="w-20">
                    <div className="flex justify-between text-xs text-gray-400 mb-0.5">
                      <span>Occ.</span>
                      <span className="font-semibold text-gray-600">{occupancyPct}%</span>
                    </div>
                    <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${occupancyPct}%`,
                          backgroundColor: occupancyPct > 90 ? '#dc2626' : occupancyPct > 70 ? '#d97706' : '#16a34a',
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => handleScopeSelect(farm.id, null)}
                    className={`text-xs px-2.5 py-1.5 rounded-lg font-semibold transition-colors ${
                      isScoped && !scopeContext.branchId
                        ? 'bg-green-600 text-white' :'bg-gray-100 text-gray-600 hover:bg-green-100 hover:text-green-700'
                    }`}
                    title="Set as active scope"
                  >
                    {isScoped && !scopeContext.branchId ? 'Scoped' : 'Set Scope'}
                  </button>
                  <button
                    onClick={() => setSelectedFarm(farm)}
                    className="btn-ghost p-1.5"
                    title="View farm profile"
                  >
                    <Eye size={14} />
                  </button>
                  <div className="relative">
                    <button
                      onClick={() => setOpenMenuId(openMenuId === farm.id ? null : farm.id)}
                      className="btn-ghost p-1.5"
                    >
                      <MoreVertical size={14} />
                    </button>
                    {openMenuId === farm.id && (
                      <div className="absolute right-0 top-full mt-1 bg-white border border-gray-100 rounded-xl shadow-lg z-20 py-1 w-40">
                        <button
                          onClick={() => { setSelectedFarm(farm); setOpenMenuId(null); }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
                        >
                          <Eye size={12} />View Profile
                        </button>
                        <button
                          onClick={() => {
                            setCreateBranchFarmId(farm.id);
                            setShowCreateBranch(true);
                            setOpenMenuId(null);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
                        >
                          <GitBranch size={12} />Add Branch
                        </button>
                        <button
                          onClick={() => setOpenMenuId(null)}
                          className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
                        >
                          <Edit2 size={12} />Edit Farm
                        </button>
                        <div className="border-t border-gray-100 my-1" />
                        <button
                          onClick={() => setOpenMenuId(null)}
                          className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-500 hover:bg-red-50"
                        >
                          <Trash2 size={12} />Deactivate
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Branches (nested) */}
              {isExpanded && (
                <div className="border-t border-gray-50 bg-gray-50/60">
                  {farm.branches.length === 0 ? (
                    <div className="flex items-center gap-3 px-6 py-3">
                      <div className="w-px h-6 bg-gray-200 ml-3 flex-shrink-0" />
                      <p className="text-xs text-gray-400 italic">No branches yet.</p>
                      <button
                        onClick={() => { setCreateBranchFarmId(farm.id); setShowCreateBranch(true); }}
                        className="text-xs text-green-600 font-semibold hover:text-green-800"
                      >
                        + Add Branch
                      </button>
                    </div>
                  ) : (
                    <div className="py-2">
                      {farm.branches.map((branch, idx) => {
                        const isBranchScoped = scopeContext.farmId === farm.id && scopeContext.branchId === branch.id;
                        const branchOcc = branch.capacity > 0 ? Math.round((branch.currentBirds / branch.capacity) * 100) : 0;
                        return (
                          <div
                            key={branch.id}
                            className={`flex items-center gap-3 px-4 py-3 mx-2 mb-1 rounded-xl transition-colors ${isBranchScoped ? 'bg-green-50 ring-1 ring-green-300' : 'hover:bg-white'}`}
                          >
                            {/* Tree line */}
                            <div className="flex items-center gap-1 flex-shrink-0 ml-6">
                              <div className="w-px h-4 bg-gray-200" />
                              <div className="w-3 h-px bg-gray-200" />
                            </div>

                            {/* Branch icon */}
                            <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                              <GitBranch size={13} className="text-blue-600" />
                            </div>

                            {/* Branch info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs font-bold text-gray-800">{branch.branchName}</span>
                                <span className="text-xs text-gray-400 font-mono">{branch.branchCode}</span>
                                <span className={`status-badge ${STATUS_COLORS[branch.status]}`}>{branch.status.charAt(0).toUpperCase() + branch.status.slice(1)}</span>
                                {isBranchScoped && <span className="status-badge bg-green-600 text-white">Scoped</span>}
                              </div>
                              <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                                <span className="flex items-center gap-1 text-xs text-gray-400">
                                  <MapPin size={10} />{branch.village}, {branch.district}
                                </span>
                                <span className="flex items-center gap-1 text-xs text-gray-400">
                                  <Users size={10} />{branch.manager}
                                </span>
                              </div>
                            </div>

                            {/* Branch stats */}
                            <div className="hidden md:flex items-center gap-4 flex-shrink-0">
                              <div className="text-center">
                                <p className="text-xs font-bold text-gray-700">{branch.currentBirds.toLocaleString()}</p>
                                <p className="text-xs text-gray-400">birds</p>
                              </div>
                              <div className="text-center">
                                <p className="text-xs font-bold text-gray-700">{branch.activeHouses}</p>
                                <p className="text-xs text-gray-400">houses</p>
                              </div>
                              <div className="w-16">
                                <div className="flex justify-between text-xs text-gray-400 mb-0.5">
                                  <span>Occ.</span>
                                  <span className="font-semibold text-gray-600">{branchOcc}%</span>
                                </div>
                                <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                  <div
                                    className="h-full rounded-full"
                                    style={{ width: `${branchOcc}%`, backgroundColor: branchOcc > 90 ? '#dc2626' : '#16a34a' }}
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Branch scope button */}
                            <button
                              onClick={() => handleScopeSelect(farm.id, branch.id)}
                              className={`text-xs px-2.5 py-1.5 rounded-lg font-semibold transition-colors flex-shrink-0 ${
                                isBranchScoped
                                  ? 'bg-green-600 text-white' :'bg-gray-100 text-gray-600 hover:bg-green-100 hover:text-green-700'
                              }`}
                            >
                              {isBranchScoped ? 'Scoped' : 'Set Scope'}
                            </button>
                          </div>
                        );
                      })}

                      {/* Add branch button */}
                      <div className="flex items-center gap-2 px-4 py-2 mx-2">
                        <div className="ml-6 flex items-center gap-1 flex-shrink-0">
                          <div className="w-px h-4 bg-gray-200" />
                          <div className="w-3 h-px bg-gray-200" />
                        </div>
                        <button
                          onClick={() => { setCreateBranchFarmId(farm.id); setShowCreateBranch(true); }}
                          className="flex items-center gap-1.5 text-xs text-green-600 font-semibold hover:text-green-800 transition-colors"
                        >
                          <Plus size={12} />Add Branch
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Modals */}
      {selectedFarm && (
        <FarmProfileModal
          farm={selectedFarm}
          onClose={() => setSelectedFarm(null)}
          onEditFarm={() => setSelectedFarm(null)}
          onAddBranch={(farmId) => {
            setCreateBranchFarmId(farmId);
            setSelectedFarm(null);
            setShowCreateBranch(true);
          }}
        />
      )}

      {showCreateFarm && (
        <CreateFarmModal
          onClose={() => setShowCreateFarm(false)}
          onSave={handleSaveFarm}
        />
      )}

      {showCreateBranch && (
        <CreateBranchModal
          farmId={createBranchFarmId}
          onClose={() => setShowCreateBranch(false)}
          onSave={handleSaveBranch}
        />
      )}

      {/* Click outside to close menu */}
      {openMenuId && (
        <div className="fixed inset-0 z-10" onClick={() => setOpenMenuId(null)} />
      )}
    </div>
  );
}
